"use client";

import { useState } from "react";
import { Target, Briefcase, Brain, Rocket, PauseCircle, AlertCircle, Compass, TrendingUp, AlertTriangle, Meh, Users, CheckCircle, HelpCircle, XCircle, Heart, DollarSign, Star, Scale, Home, Battery, Repeat } from "lucide-react";

// Icon mapping for the theme
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  target: Target,
  briefcase: Briefcase,
  brain: Brain,
  rocket: Rocket,
  "pause-circle": PauseCircle,
  "alert-circle": AlertCircle,
  "compass": Compass,
  "trending-up": TrendingUp,
  "alert-triangle": AlertTriangle,
  "meh": Meh,
  users: Users,
  "check-circle": CheckCircle,
  "help-circle": HelpCircle,
  "x-circle": XCircle,
  heart: Heart,
  "dollar-sign": DollarSign,
  star: Star,
  scale: Scale,
  home: Home,
  battery: Battery,
  repeat: Repeat,
};

type Option = {
  value: string;
  icon?: string;
  description?: string;
};

type Props = {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  name: string;
};

export default function IconCardThemeRadio({ options, value, onChange }: Props) {
  const [showFeedback, setShowFeedback] = useState(false);

  const handleSelect = (optionValue: string) => {
    setShowFeedback(false);
    onChange(optionValue);
    
    // Show feedback after a brief delay
    setTimeout(() => {
      setShowFeedback(true);
    }, 300);
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4 max-w-2xl mx-auto">
        {options.map((option) => {
          const Icon = option.icon ? iconMap[option.icon] : null;
          const isSelected = value === option.value;
          
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`p-3 sm:p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                isSelected
                  ? "border-[#2A6354] bg-[#2A6354]/5 shadow-lg scale-105"
                  : "border-[#BFBCC0]/30 bg-white hover:border-[#BFBCC0]/50 hover:shadow-md"
              }`}
              role="radio"
              aria-checked={isSelected}
            >
              <div className="flex items-center sm:items-start gap-3 sm:gap-4">
                {Icon && (
                  <div
                    className={`p-2 sm:p-3 rounded-lg transition-colors flex-shrink-0 ${
                      isSelected
                        ? "bg-[#2A6354]/10"
                        : "bg-[#D2CED4]/20"
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 sm:w-6 sm:h-6 ${
                        isSelected
                          ? "text-[#2A6354]"
                          : "text-[#000000]/60"
                      }`}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <p
                    className={`${
                      isSelected
                        ? "text-[#2A6354]"
                        : "text-[#000000]"
                    } ${option.description ? 'font-medium' : ''}`}
                    style={{ fontFamily: 'Barlow, sans-serif' }}
                  >
                    {option.value}
                  </p>
                  {option.description && (
                    <p
                      className={`text-sm mt-1 ${
                        isSelected
                          ? "text-[#2A6354]/70"
                          : "text-[#000000]/60"
                      }`}
                      style={{ fontFamily: 'Barlow, sans-serif' }}
                    >
                      {option.description}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Feedback Message */}
      {value && (
        <div
          className={`mt-4 sm:mt-8 p-4 sm:p-6 bg-gradient-to-r from-[#2A6354]/5 to-[#EBCC5E]/5 rounded-lg border border-[#2A6354]/20 transition-all duration-500 ${
            showFeedback
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          }`}
        >
          <p className="text-center text-base sm:text-lg font-medium text-[#000000]" style={{ fontFamily: 'Barlow, sans-serif' }}>
            <span className="text-xl sm:text-2xl mr-2">✨</span>
            Excellent choice! Your motivation will fuel your transformation.
          </p>
        </div>
      )}
    </div>
  );
}